/**
 * Environment detection utilities for determining the runtime environment
 * (web browser vs Electron) and providing environment-specific functionality.
 */

/**
 * Supported runtime environments
 */
export type Environment = "web" | "electron" | "unknown";

/**
 * Environment detection result with additional metadata
 */
export interface EnvironmentInfo {
  /** The detected environment type */
  environment: Environment;
  /** Whether running in Electron */
  isElectron: boolean;
  /** Whether running in a web browser */
  isWeb: boolean;
  /** Electron version if available */
  electronVersion?: string;
  /** Node.js version if available */
  nodeVersion?: string;
  /** User agent string if available */
  userAgent?: string;
  /** Whether running in development mode */
  isDevelopment: boolean;
  /** Whether running in production mode */
  isProduction: boolean;
}

export type ElectronSharedUIAPIs = {
  ipcRenderer?: {
    send: (channel: string, ...args: any[]) => void;
    on: (channel: string, listener: (...args: any[]) => void) => void;
    removeListener: (channel: string, listener: (...args: any[]) => void) => void;
  };
  shell?: {
    openExternal: (url: string) => Promise<void>;
  };
};

/**
 * Global type declarations for environment-specific globals
 */
declare global {
  interface Window {
    electron: ElectronSharedUIAPIs;
    process?: {
      versions?: {
        electron?: string;
        node?: string;
      };
      env?: {
        NODE_ENV?: string;
        ELECTRON_RENDERER_URL?: string;
        [key: string]: string | undefined;
      };
    };
    /**
     * Runtime environment variables injected by Docker entrypoint script (web builds)
     * These are set at container startup and take precedence over build-time vars
     */
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_BETTER_AUTH_URL?: string;
      VITE_POSTHOG_KEY?: string;
      VITE_POSTHOG_HOST?: string;
      VITE_APP_ENV?: string;
      [key: string]: string | undefined;
    };
  }
}

/**
 * Detects the current runtime environment
 * @returns EnvironmentInfo object with environment details
 */
export function getEnvironment(): EnvironmentInfo {
  const isElectron = detectElectron();
  const isWeb = !isElectron && typeof window !== "undefined";
  const environment: Environment = isElectron ? "electron" : isWeb ? "web" : "unknown";

  const electronVersion = getElectronVersion();
  const nodeVersion = getNodeVersion();
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : undefined;
  const isDevelopment = getIsDevelopment();
  const isProduction = !isDevelopment;

  return {
    environment,
    isElectron,
    isWeb,
    electronVersion,
    nodeVersion,
    userAgent,
    isDevelopment,
    isProduction,
  };
}

/**
 * Detects if running in Electron environment
 * @returns true if running in Electron, false otherwise
 */
function detectElectron(): boolean {
  // Check for Electron-specific globals
  if (typeof window !== "undefined") {
    // Check for window.electron (exposed via contextBridge)
    if (window.electron) {
      return true;
    }

    // Check for Electron-specific properties in window
    if ("process" in window && window.process?.versions?.electron) {
      return true;
    }

    // Check for Electron-specific user agent
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("Electron")) {
      return true;
    }
  }

  // Check for Node.js process in renderer (Electron-specific)
  if (typeof process !== "undefined" && process.versions?.electron) {
    return true;
  }

  return false;
}

/**
 * Gets Electron version if available
 * @returns Electron version string or undefined
 */
function getElectronVersion(): string | undefined {
  if (typeof window !== "undefined" && window.process?.versions?.electron) {
    return window.process.versions.electron;
  }

  if (typeof process !== "undefined" && process.versions?.electron) {
    return process.versions.electron;
  }

  return undefined;
}

/**
 * Gets Node.js version if available
 * @returns Node.js version string or undefined
 */
function getNodeVersion(): string | undefined {
  if (typeof window !== "undefined" && window.process?.versions?.node) {
    return window.process.versions.node;
  }

  if (typeof process !== "undefined" && process.versions?.node) {
    return process.versions.node;
  }

  return undefined;
}

/**
 * Determines if running in development mode
 * @returns true if in development, false otherwise
 */
function getIsDevelopment(): boolean {
  // Check NODE_ENV
  if (typeof window !== "undefined" && window.process?.env?.NODE_ENV === "development") {
    return true;
  }

  if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
    return true;
  }

  // Check for development indicators
  if (typeof window !== "undefined") {
    // Check for Vite dev server
    if (window.location?.hostname === "localhost" || window.location?.hostname === "127.0.0.1") {
      return true;
    }

    // Check for Electron dev URL
    if (window.process?.env?.ELECTRON_RENDERER_URL) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a specific feature is available in the current environment
 * @param feature - The feature to check for
 * @returns true if feature is available, false otherwise
 */
export function isFeatureAvailable(
  feature: "ipc" | "shell" | "fileSystem" | "nativeMenus",
): boolean {
  const env = getEnvironment();

  if (!env.isElectron) {
    return false;
  }

  switch (feature) {
    case "ipc":
      return typeof window !== "undefined" && !!window.electron?.ipcRenderer;
    case "shell":
      return typeof window !== "undefined" && !!window.electron?.shell;
    case "fileSystem":
      // File system access is typically available in Electron
      return env.isElectron;
    case "nativeMenus":
      // Native menus are available in Electron
      return env.isElectron;
    default:
      return false;
  }
}

/**
 * Gets environment-specific configuration
 * @returns Configuration object with environment-specific settings
 */
export function getEnvironmentConfig() {
  const env = getEnvironment();

  return {
    // Navigation settings
    navigation: {
      showElectronSpecificItems: env.isElectron,
      showWebSpecificItems: env.isWeb,
      enableKeyboardShortcuts: env.isElectron, // Better keyboard support in Electron
    },

    // UI settings
    ui: {
      enableNativeMenus: env.isElectron,
      enableContextMenus: env.isElectron,
      enableDragAndDrop: env.isElectron,
    },

    // Development settings
    development: {
      enableDevTools: env.isDevelopment,
      enableHotReload: env.isDevelopment,
      enableDebugLogging: env.isDevelopment,
    },
  };
}

/**
 * Gets an environment variable value, supporting multiple runtime environments.
 *
 * Priority order:
 * 1. window.__ENV__[key] (runtime injected in Docker/production web builds)
 * 2. import.meta.env[key] (Vite build-time variables - works in web dev, web build, and Electron renderer)
 * 3. window.process.env[key] (Electron renderer fallback)
 * 4. process.env[key] (Node.js/Electron main process fallback)
 *
 * This function works across:
 * - Vite dev environment (web)
 * - Vite static build (web)
 * - Electron renderer process
 * - Electron main process (though this is less common for UI code)
 *
 * @param key - The environment variable key (e.g., "VITE_ENABLE_UNIVERSAL_SEARCH" or "NODE_ENV")
 * @returns The environment variable value or undefined if not found
 */
export function getEnvVar(key: string): string | undefined {
  // 1. Check runtime injected variables first (from Docker container env vars in web builds)
  if (typeof window !== "undefined" && window.__ENV__?.[key]) {
    return window.__ENV__[key];
  }

  // 2. Check Vite's import.meta.env (works in Vite dev, Vite build, and Electron renderer)
  if (typeof import.meta !== "undefined" && "env" in import.meta) {
    const viteEnv = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
    if (viteEnv[key]) {
      return viteEnv[key];
    }
  }

  // 3. Check window.process.env (Electron renderer fallback)
  if (typeof window !== "undefined" && window.process?.env?.[key]) {
    return window.process.env[key];
  }

  // 4. Check process.env (Node.js/Electron main process fallback)
  if (typeof process !== "undefined" && process.env?.[key]) {
    return process.env[key];
  }

  return undefined;
}
